import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import type { Poll, PollStatus } from './types';

// Poll history will be retained for 1 hour
export const DEFAULT_POLL_HISTORY_TTL_MS = 60 * 60 * 1000;

export interface PollHistoryEntry {
  poll: Poll;
  status: PollStatus;
  completedAt: number;
  expiresAt: number;
  error?: string;
}

// Base poll history atom with localStorage persistence
const basePollHistoryAtom = atomWithStorage<PollHistoryEntry[]>(
  'socrative_poll_history',
  []
);

// Derived poll history atom that filters out expired entries
export const pollHistoryAtom = atom(
  (get) => {
    const history = get(basePollHistoryAtom);
    const now = Date.now();
    return history.filter((entry) => entry.expiresAt && entry.expiresAt > now);
  },
  (get, set, newHistory: PollHistoryEntry[]) => {
    // Filter out expired entries before writing to localStorage
    const now = Date.now();
    const validEntries = newHistory.filter(
      (entry) => entry.expiresAt && entry.expiresAt > now
    );
    set(basePollHistoryAtom, validEntries);
  }
);

interface AddPollToHistoryParams {
  poll: Poll;
  status: PollStatus;
  error?: string;
  ttlMs?: number;
}

// Helper to add a poll to the history
export const addPollToHistoryAtom = atom(
  null,
  (get, set, entry: AddPollToHistoryParams) => {
    const current = get(pollHistoryAtom);
    const now = Date.now();
    const ttl = entry.ttlMs ?? DEFAULT_POLL_HISTORY_TTL_MS;
    const expiresAt = now + ttl;

    if (!entry.poll?.id) return;

    const existingIndex = current.findIndex(
      (item) => item.poll.id === entry.poll.id
    );

    if (existingIndex === -1) {
      set(pollHistoryAtom, [
        ...current,
        {
          poll: entry.poll,
          status: entry.status,
          error: entry.error,
          completedAt: now,
          expiresAt,
        },
      ]);
    } else {
      const existingEntry = current[existingIndex];
      if (
        existingEntry.status !== entry.status ||
        existingEntry.error !== entry.error
      ) {
        set(
          pollHistoryAtom,
          current.map((item) =>
            item.poll.id === entry.poll.id
              ? {
                  ...item,
                  status: entry.status,
                  error: entry.error,
                  completedAt: now,
                  expiresAt,
                }
              : item
          )
        );
      }
    }
  }
);

// Helper to remove a poll from history by requestId
export const removeFromHistoryAtom = atom(
  null,
  (get, set, requestId: string) => {
    if (!requestId) return;
    const current = get(pollHistoryAtom);
    set(
      pollHistoryAtom,
      current.filter((entry) => entry.poll.metadata?.requestId !== requestId)
    );
  }
);
